import { useSyncExternalStore } from 'react';

export type ThemePref = 'system' | 'light' | 'dark';
export type Density = 'comfortable' | 'compact';
export type Locale = 'fr' | 'en' | 'ar';
export const LOCALES: Locale[] = ['fr', 'en', 'ar'];

interface Prefs { theme: ThemePref; density: Density; locale: Locale; sidebarCollapsed: boolean }

const read = <T extends string>(key: string, allowed: readonly T[], fallback: T): T => {
  try { const v = localStorage.getItem(key); return allowed.includes(v as T) ? (v as T) : fallback; } catch { return fallback; }
};

let state: Prefs = {
  theme: read('theme', ['system', 'light', 'dark'], 'system'),
  density: read('density', ['comfortable', 'compact'], 'comfortable'),
  locale: read('locale', LOCALES, 'fr'),
  sidebarCollapsed: (() => { try { return localStorage.getItem('sidebarCollapsed') === '1'; } catch { return false; } })(),
};
const listeners = new Set<() => void>();
const media = typeof matchMedia === 'function' ? matchMedia('(prefers-color-scheme: dark)') : null;

function apply() {
  const root = document.documentElement;
  const dark = state.theme === 'dark' || (state.theme === 'system' && !!media?.matches);
  root.dataset.theme = dark ? 'dark' : 'light';
  root.dataset.density = state.density;
  root.lang = state.locale;
  root.dir = state.locale === 'ar' ? 'rtl' : 'ltr';
}

function set(patch: Partial<Prefs>) {
  state = { ...state, ...patch };
  try {
    localStorage.setItem('theme', state.theme); localStorage.setItem('density', state.density); localStorage.setItem('locale', state.locale);
    localStorage.setItem('sidebarCollapsed', state.sidebarCollapsed ? '1' : '0');
  } catch { /* private mode: preferences last for the session only */ }
  apply();
  listeners.forEach((l) => l());
}

media?.addEventListener?.('change', () => { if (state.theme === 'system') apply(); });
if (typeof document !== 'undefined') apply();

export const preferences = {
  get: () => state,
  set,
  subscribe(l: () => void) { listeners.add(l); return () => { listeners.delete(l); }; },
};

export const usePreferences = () => useSyncExternalStore(preferences.subscribe, preferences.get, preferences.get);
